'use client';

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface BetterStackModalProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode?: boolean;
}

export default function BetterStackModal({
  isOpen,
  onClose,
  darkMode = false,
}: BetterStackModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full max-w-5xl transform overflow-hidden rounded-lg shadow-xl transition-all ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}
              >
                {/* Header */}
                <div
                  className={`flex items-center justify-between border-b px-6 py-4 ${
                    darkMode ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  <Dialog.Title
                    className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    System Status
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className={`rounded-lg p-2 transition-colors ${
                      darkMode
                        ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Iframe Content */}
                <div className="relative" style={{ height: '70vh' }}>
                  <iframe
                    src={`https://status.valiyou.com/?theme=${darkMode ? 'dark' : 'light'}`}
                    className="h-full w-full border-0"
                    title="Valiyou Status"
                  />
                </div>

                {/* Footer */}
                <div
                  className={`border-t px-6 py-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
                >
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Real-time system status and incident history powered by Better Stack.
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
